import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip, Link, InputBase, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import moment from "moment-timezone";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { BG_TINT, BORDER, RADIUS_INPUT } from "components/bento/bentoTokens";
import { GetSalesDocumentList } from "api/salesDocument";
import DocumentDetailDialog from "./DocumentDetailDialog";

const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const DOC_TYPE_LABEL = { receipt: "docTypeReceipt", quotation: "docTypeQuotation", invoice: "docTypeInvoice" };

// `refreshSignal` — bumped by the parent whenever a new document is generated
// on the Create tab, so it shows up here without a manual reload.
// `onDuplicate` — forwarded to DocumentDetailDialog's "Duplicate" action; the
// parent (scenes/salesDocuments/index.jsx) owns switching to the Create tab.
const HistoryTab = ({ refreshSignal, onDuplicate }) => {
  const { t } = useTranslation();

  const [docType, setDocType] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDocId, setOpenDocId] = useState(null);

  const refreshList = () => {
    setIsLoading(true);
    GetSalesDocumentList({ docType: docType || undefined, search: search || undefined }).then((res) => {
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, refreshSignal]);

  const columns = [
    {
      field: "docNumber", headerName: t("salesDocuments.historyColumnDocNumber"), flex: 1,
      renderCell: (params) => (
        <Link component="button" color="secondary" underline="hover" onClick={() => setOpenDocId(params.row._id)}>
          {params.row.docNumber}
        </Link>
      ),
    },
    {
      field: "docType", headerName: t("salesDocuments.historyColumnDocType"), flex: 0.8,
      valueGetter: (params) => t(`salesDocuments.${DOC_TYPE_LABEL[params.row.docType]}`),
    },
    { field: "customerName", headerName: t("salesDocuments.historyColumnCustomer"), flex: 1, valueGetter: (params) => params.row.customer?.name || "" },
    {
      field: "date", headerName: t("salesDocuments.historyColumnDate"), flex: 0.8,
      valueGetter: (params) => (params.row.date ? moment(params.row.date).tz("Asia/Vientiane").format("DD/MM/YYYY") : ""),
    },
    {
      field: "totalAmount", headerName: t("salesDocuments.historyColumnTotal"), flex: 0.8,
      valueGetter: (params) => `${SYM[params.row.currency] || ""}${(Number(params.row.totalAmount) || 0).toLocaleString(undefined, params.row.currency === "LAK" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2 })}`,
    },
    {
      field: "status", headerName: t("salesDocuments.historyColumnStatus"), flex: 0.8,
      renderCell: (params) => <Chip label={t(`salesDocuments.status${params.value}`, params.value)} size="small" color="secondary" variant="outlined" />,
    },
  ];

  return (
    <>
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel shrink>{t("salesDocuments.docType")}</InputLabel>
            <Select label={t("salesDocuments.docType")} value={docType} onChange={(e) => setDocType(e.target.value)} displayEmpty>
              <MenuItem value="">{t("salesDocuments.filterAllTypes")}</MenuItem>
              <MenuItem value="receipt">{t("salesDocuments.docTypeReceipt")}</MenuItem>
              <MenuItem value="quotation">{t("salesDocuments.docTypeQuotation")}</MenuItem>
              <MenuItem value="invoice">{t("salesDocuments.docTypeInvoice")}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{
            display: "flex", alignItems: "center", flex: "1 1 260px", minWidth: 220, height: "40px",
            background: BG_TINT, border: `1px solid ${BORDER}`, borderRadius: RADIUS_INPUT, px: "12px",
          }}>
            <InputBase
              placeholder={t("salesDocuments.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { refreshList(); e.preventDefault(); } }}
              sx={{ flex: 1, fontSize: "0.9rem" }}
            />
            <IconButton size="small" onClick={refreshList}><SearchIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </BentoCard>

      {!isLoading && rows.length === 0 ? (
        <BentoCard bodySx={{ p: "48px 22px", textAlign: "center" }}>
          <DescriptionIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
          <Typography color="text.secondary">{t("salesDocuments.noDocuments")}</Typography>
        </BentoCard>
      ) : (
        <Box height="70vh" width="100%">
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id}
            rows={rows}
            columns={columns}
            onRowClick={(params) => setOpenDocId(params.row._id)}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </Box>
      )}

      <DocumentDetailDialog
        open={!!openDocId}
        docId={openDocId}
        onClose={() => setOpenDocId(null)}
        onChanged={refreshList}
        onDuplicate={(doc) => { setOpenDocId(null); onDuplicate?.(doc); }}
      />
    </>
  );
};

export default HistoryTab;
